/**
 * Recently Viewed — per-user localStorage bucket.
 *
 * Hardened against cross-user data leakage:
 *   • ALL reads/writes require a non-empty userId. When no userId is
 *     provided, every function is a no-op (reads return [], writes
 *     silently drop). There is NO unscoped fallback key any more.
 *   • Bucket key is versioned: `wassla_recently_viewed_v2_<userId>`.
 *     The v2 bump invalidates v1 / legacy unscoped buckets from earlier
 *     builds so nobody can read data written by the unsafe code.
 *   • `sweepLegacyRecentlyViewedKeys()` removes every pre-v2 bucket
 *     (shared and per-user). It is called at logout AND at app boot
 *     so stale data cannot survive a refresh.
 *
 * Why this matters:
 *   The previous implementation returned a shared key whenever userId
 *   was falsy (first paint, anonymous browsing, race with AuthContext).
 *   Two different users on the same device would then see each other's
 *   history. This module closes that hole at the source.
 */

const PREFIX_V2 = 'wassla_recently_viewed_v2_';
const LEGACY_PREFIX_V1 = 'wassla_recently_viewed_';   // old per-user prefix
const LEGACY_SHARED_KEY = 'wassla_recently_viewed';   // old shared bucket
const MAX = 20;

function isValidUserId(userId) {
  return typeof userId === 'string' && userId.trim().length > 0;
}

function getKey(userId) {
  return isValidUserId(userId) ? `${PREFIX_V2}${userId}` : null;
}

/** Remove every legacy bucket (unscoped, v1 per-user). Safe to call anywhere. */
export function sweepLegacyRecentlyViewedKeys() {
  try {
    localStorage.removeItem(LEGACY_SHARED_KEY);
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k) continue;
      // Old v1 buckets used `wassla_recently_viewed_<userId>` with NO v2
      // segment. The v2 prefix (`wassla_recently_viewed_v2_`) is left
      // alone because it's the new safe format.
      if (k.startsWith(LEGACY_PREFIX_V1) && !k.startsWith(PREFIX_V2)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* storage unavailable — ignore */
  }
}

/** Remove every recently-viewed bucket regardless of user (logout). */
export function purgeAllRecentlyViewed() {
  try {
    sweepLegacyRecentlyViewedKeys();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX_V2)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* storage unavailable — ignore */
  }
}

/**
 * Remove the given ad_ids from EVERY user's recently-viewed bucket on
 * this device. Used by the admin cleanup tool after a deletion so the
 * stale ad_ids don't linger in the carousel cache.
 *
 * Strictly per-device: does NOT reach other browsers / sessions.
 * Buckets that become empty are removed entirely.
 */
export function removeAdsFromAllRecentlyViewed(adIds) {
  if (!Array.isArray(adIds) || adIds.length === 0) return 0;
  const toRemove = new Set(adIds.filter((x) => typeof x === 'string' && x));
  if (toRemove.size === 0) return 0;
  let scrubbed = 0;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PREFIX_V2)) continue;
      let list;
      try {
        const raw = localStorage.getItem(k);
        list = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(list)) list = [];
      } catch {
        list = [];
      }
      const before = list.length;
      const next = list.filter((id) => !toRemove.has(id));
      if (next.length === before) continue;
      scrubbed += before - next.length;
      if (next.length === 0) {
        localStorage.removeItem(k);
      } else {
        localStorage.setItem(k, JSON.stringify(next));
      }
    }
  } catch {
    /* storage unavailable — ignore */
  }
  return scrubbed;
}

export function addRecentlyViewed(adId, userId) {
  if (!adId || !isValidUserId(userId)) return;
  const key = getKey(userId);
  if (!key) return;
  try {
    const list = getRecentlyViewed(userId);
    const filtered = list.filter((id) => id !== adId);
    const next = [adId, ...filtered].slice(0, MAX);
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function getRecentlyViewed(userId) {
  if (!isValidUserId(userId)) return [];
  const key = getKey(userId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string' && id) : [];
  } catch {
    return [];
  }
}

export function clearRecentlyViewed(userId) {
  if (!isValidUserId(userId)) return;
  const key = getKey(userId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
